customElements.whenDefined('custom-select').then(() => {
  if (customElements.get('country-selector')) return;

  class CountrySelector extends customElements.get('custom-select') {
    constructor() {
      super();
      this.loaded = false;
      this.panel = this.querySelector('.country-selector__panel');
      this.searchInput = this.querySelector('.country-selector__input');
      this.clearButton = this.querySelector('.country-selector__clear');
      this.emptyMessage = this.querySelector('.country-selector__empty');
      this.emptyText = this.emptyMessage.textContent;
      this.positionHandler = this.positionPanel.bind(this);
      this.outsideHandler = (evt) => {
        if (!this.contains(evt.target)) this.hideListbox(false);
      };
      this.button.setAttribute('aria-controls', this.listbox.id);
      this.button.addEventListener('click', () => {
        if (this.listboxOpen) this.hideListbox();
        else {
          this.button.focus({ preventScroll: true });
          this.showListbox();
        }
      });
      this.searchInput.addEventListener('input', () => this.filterOptions());
      this.searchInput.addEventListener('change', (evt) => evt.stopPropagation());
      this.panel.addEventListener('mousedown', (evt) => {
        if (evt.target !== this.searchInput) evt.preventDefault();
      });
      this.clearButton.addEventListener('click', () => {
        this.searchInput.value = '';
        this.filterOptions();
        this.searchInput.focus({ preventScroll: true });
      });
      this.listbox.addEventListener('click', (evt) => {
        const option = evt.target.closest('.custom-select__option');
        if (option && this.listbox.contains(option)) this.selectOption(option);
      });
      this.listbox.addEventListener('mouseover', (evt) => {
        const option = evt.target.closest('.custom-select__option');
        if (option && !option.hidden) this.focusOption(option);
      });
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          if (this.listboxOpen && !this.contains(document.activeElement)) this.hideListbox(false);
        });
      });
    }

    handleMousedown() {}

    async showListbox() {
      if (this.loading || this.listboxOpen) return;
      if (!this.loaded) {
        this.loading = true;
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');
        try {
          this.loadError = false;
          const response = await fetch(this.dataset.countriesUrl);
          if (!response.ok) throw new Error(response.status);
          const template = document.createElement('template');
          template.innerHTML = await response.text();
          const listbox = template.content.querySelector('.custom-select__listbox');
          if (!listbox?.querySelector('.custom-select__option')) throw new Error('No countries');
          this.listbox.innerHTML = listbox.innerHTML;
          this.options = this.listbox.querySelectorAll('.custom-select__option');
          let englishNames;
          try {
            englishNames = new Intl.DisplayNames(['en'], { type: 'region' });
          } catch {}
          this.options.forEach((option, index) => {
            option.id = `${this.listbox.id}-option-${index}`;
            const name = option.querySelector('.country-option__name')?.textContent || option.textContent;
            option.dataset.search = this.normalize(`${name} ${option.dataset.value} ${englishNames?.of(option.dataset.value) || ''}`);
          });
          const popular = this.listbox.querySelectorAll('[data-popular]');
          popular[popular.length - 1]?.closest('.custom-select__option').classList.add('custom-select__option--visual-group-end');
          const countryCode = this.closest('.localization__selector').querySelector('input[name="country_code"]').value;
          this.selectedOption = Array.from(this.options).find((option) => option.dataset.value === countryCode) || this.options[0];
          this.options.forEach((option) => option.setAttribute('aria-selected', String(option === this.selectedOption)));
          this.loaded = true;
        } catch {
          this.loadError = true;
        } finally {
          this.loading = false;
          this.button.classList.remove('is-loading');
          this.button.setAttribute('aria-disabled', 'false');
        }
      }
      if (document.activeElement !== this.button && !this.contains(document.activeElement)) return;
      this.searchInput.value = '';
      this.panel.hidden = false;
      this.listbox.hidden = this.loadError;
      this.listboxOpen = true;
      this.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');
      this.searchInput.setAttribute('aria-expanded', 'true');
      this.listbox.setAttribute('aria-hidden', 'false');
      this.searchInput.disabled = this.loadError;
      this.emptyMessage.textContent = this.loadError ? this.dataset.loadingError : this.emptyText;
      if (this.loadError) this.emptyMessage.hidden = false;
      else this.filterOptions();
      this.positionPanel();
      if (!this.loadError) {
        this.focusOption(this.selectedOption);
        this.searchInput.focus({ preventScroll: true });
      }
      document.addEventListener('pointerdown', this.outsideHandler);
      window.addEventListener('resize', this.positionHandler);
      window.visualViewport?.addEventListener('resize', this.positionHandler);
      window.visualViewport?.addEventListener('scroll', this.positionHandler);
    }

    hideListbox(restoreFocus = true) {
      if (!this.listboxOpen) return;
      this.listboxOpen = false;
      this.panel.hidden = true;
      this.listbox.hidden = true;
      this.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
      this.searchInput.setAttribute('aria-expanded', 'false');
      this.listbox.setAttribute('aria-hidden', 'true');
      this.focusedOption?.classList.remove(this.focusedClass);
      this.focusedOption = null;
      this.searchInput.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', this.outsideHandler);
      window.removeEventListener('resize', this.positionHandler);
      window.visualViewport?.removeEventListener('resize', this.positionHandler);
      window.visualViewport?.removeEventListener('scroll', this.positionHandler);
      if (restoreFocus) this.button.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.hideListbox(false);
    }

    normalize(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    }

    filterOptions() {
      const query = this.normalize(this.searchInput.value);
      this.clearButton.hidden = !this.searchInput.value;
      const terms = query.split(/\s+/).filter(Boolean);
      const visible = [];
      this.options.forEach((option) => {
        option.hidden = !terms.every((term) => option.dataset.search.includes(term));
        option.classList.toggle('country-selector__filtered', !!query);
        if (!option.hidden) visible.push(option);
      });
      this.emptyMessage.hidden = visible.length > 0;
      const next = visible.includes(this.selectedOption) && !query ? this.selectedOption : visible[0];
      this.focusOption(next);
      if (query) this.listbox.scrollTop = 0;
    }

    focusOption(option) {
      if (!option || option.hidden) {
        this.focusedOption?.classList.remove(this.focusedClass);
        this.focusedOption = null;
        this.searchInput?.removeAttribute('aria-activedescendant');
        this.listbox.removeAttribute('aria-activedescendant');
        return;
      }
      super.focusOption(option);
      this.searchInput.setAttribute('aria-activedescendant', option.id);
      this.listbox.setAttribute('aria-activedescendant', option.id);
    }

    selectOption(option) {
      if (!option || option.hidden) return;
      const changed = option !== this.selectedOption;
      if (changed) {
        this.selectedOption.setAttribute('aria-selected', 'false');
        option.setAttribute('aria-selected', 'true');
        this.selectedOption = option;
        this.button.firstElementChild.textContent = option.firstElementChild.textContent;
        this.listbox.setAttribute('aria-activedescendant', option.id);
        this.closest('.localization__selector').querySelector('input[name="country_code"]').value = option.dataset.value;
      }
      this.hideListbox();
      if (changed) this.closest('form').submit();
    }

    handleKeydown(evt) {
      if (!this.listboxOpen) {
        if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp' || evt.key === ' ') {
          evt.preventDefault();
          this.showListbox();
        }
        return;
      }
      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        this.hideListbox();
      } else if (this.loadError) {
        if (evt.key === 'Enter') evt.preventDefault();
      } else if (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') {
        evt.preventDefault();
        const visible = Array.from(this.options).filter((option) => !option.hidden);
        const index = visible.indexOf(this.focusedOption);
        const next = index < 0 ? (evt.key === 'ArrowDown' ? 0 : visible.length - 1)
          : Math.max(0, Math.min(visible.length - 1, index + (evt.key === 'ArrowDown' ? 1 : -1)));
        this.focusOption(visible[next]);
      } else if (evt.key === 'Enter' && evt.target !== this.clearButton) {
        evt.preventDefault();
        this.selectOption(this.focusedOption);
      }
    }

    positionPanel() {
      if (!this.listboxOpen) return;
      const rect = this.button.getBoundingClientRect();
      const parent = this.panel.offsetParent.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const width = Math.min(340, (viewport?.width || window.innerWidth) - 32);
      const above = rect.top - top - 12;
      const below = bottom - rect.bottom - 12;
      const opensAbove = below < 320 && above > below;
      const height = Math.max(96, Math.min(400, opensAbove ? above : below));
      this.panel.style.width = `${width}px`;
      this.panel.style.maxHeight = `${height}px`;
      this.panel.style.left = `${Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)) - parent.left}px`;
      this.panel.style.top = opensAbove ? 'auto' : `${rect.bottom - parent.top + 8}px`;
      this.panel.style.bottom = opensAbove ? `${parent.bottom - rect.top + 8}px` : 'auto';
    }

    setButtonWidth() {}
  }

  customElements.define('country-selector', CountrySelector);
});
